import React, { useEffect, useState } from "react";
import { Alert, Button, LinearProgress, TextField } from "@mui/material";
import { FORMSPREE_ENDPOINT } from "../constants";
import { getCsrfToken } from "../utils/csrfProtection";
import "./ContactForm.css";

const validate = ({ name, email, message }) => {
  const errors = {};
  if (name.trim().length < 2) errors.name = "Enter your name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Enter a valid email address.";
  if (message.trim().length < 10) errors.message = "Add a little more detail.";
  return errors;
};

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [csrfToken, setCsrfToken] = useState("");

  useEffect(() => {
    setCsrfToken(getCsrfToken());
  }, []);

  const updateField = (field) => (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setStatus("");
  };

  const submit = async (event) => {
    event.preventDefault();
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, csrfToken, timestamp: new Date().toISOString() }),
      });
      if (!response.ok) throw new Error("Unable to send your message.");
      setForm({ name: "", email: "", message: "" });
      setStatus("Message sent. Thank you for reaching out.");
    } catch {
      setStatus("Message could not be sent. Please email me directly instead.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="portfolio-section contact-section" data-scene-section="contact">
      <div className="section-wrap contact-wrap" data-animate="true">
        <div className="contact-copy">
          <div className="destination-label"><span>07</span><span>Horizon beacon</span></div>
          <h2 className="section-title">Start the next expedition.</h2>
          <p className="contact-intro">
            I’m open to software engineering roles, product collaborations, and research conversations. I usually reply within two business days.
          </p>
          <a className="contact-email" href="mailto:aviral23.1999@gmail.com">aviral23.1999@gmail.com</a>
          <p className="contact-coordinate">California · Pacific Time</p>
        </div>
        <form className="contact-form" onSubmit={submit} aria-label="Contact form">
          {isSubmitting && <LinearProgress aria-label="Sending message" />}
          {status && <Alert severity={status.startsWith("Message sent") ? "success" : "error"} aria-live="polite">{status}</Alert>}
          <TextField
            label="Name"
            value={form.name}
            onChange={updateField("name")}
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
            disabled={isSubmitting}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={updateField("email")}
            error={Boolean(errors.email)}
            helperText={errors.email}
            required
            disabled={isSubmitting}
          />
          <TextField
            label="Message"
            multiline
            minRows={5}
            value={form.message}
            onChange={updateField("message")}
            error={Boolean(errors.message)}
            helperText={errors.message}
            required
            disabled={isSubmitting}
            inputProps={{ maxLength: 5000 }}
          />
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? "Sending" : "Send message"}
          </Button>
          <p className="contact-privacy">Your details are used only to reply to this message.</p>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
